import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ChevronLeft, ChevronRight, Clock, Search, SearchX, ShieldCheck, Truck, Wrench } from 'lucide-react';
import { shopApi } from '../../services/shopApi';
import PartCard from '../../components/PartCard/PartCard';
import useDebounce from '../../utils/useDebounce';
import { pluralize } from '../../utils/fromat';
import { SHOP_SECTIONS } from './ShopContent';
import './ShopCatalog.css';

const ICONS = { shield: ShieldCheck, truck: Truck, clock: Clock, wrench: Wrench };
const VEHICLE_TABS = [
    { value: '', label: 'Все' },
    { value: 'car', label: 'Авто' },
    { value: 'moto', label: 'Мото' },
];
const SORTS = [
    { value: 'featured', label: 'Сначала популярные' },
    { value: 'new', label: 'Новинки' },
    { value: 'price_asc', label: 'Сначала дешёвые' },
    { value: 'price_desc', label: 'Сначала дорогие' },
];
const URL_KEYS = ['vehicle_type', 'category', 'q', 'sort', 'page'];

// productType: 'part' | 'accessory' — раздел каталога (совпадает с product_type на бэкенде)
export default function ShopCatalog({ productType = 'part' }) {
    const section = SHOP_SECTIONS[productType] || SHOP_SECTIONS.part;
    const { hero, features } = section;

    const [sp, setSp] = useSearchParams();
    const [filters, setFilters] = useState({ categories: [], brands: [] });
    const [state, setState] = useState({ status: 'loading', items: [], meta: null });
    const [reloadKey, setReloadKey] = useState(0);
    const catalogRef = useRef(null);

    const urlQuery = sp.get('q') || '';
    const [search, setSearch] = useState(urlQuery);
    const debouncedSearch = useDebounce(search, 400);

    const params = useMemo(() => {
        const o = {};
        URL_KEYS.forEach((k) => { const v = sp.get(k); if (v) o[k] = v; });
        return o;
    }, [sp]);

    // Любое изменение фильтра сбрасывает страницу, кроме явной смены page
    const patch = useCallback((changes) => {
        const next = { ...params, ...changes };
        if (!('page' in changes)) delete next.page;
        Object.keys(next).forEach((k) => (next[k] == null || next[k] === '') && delete next[k]);
        setSp(next);
    }, [params, setSp]);

    // URL → поле поиска (кнопка «назад», сброс фильтров)
    useEffect(() => { setSearch(urlQuery); }, [urlQuery]);

    // поле поиска → URL (с задержкой)
    useEffect(() => {
        const q = debouncedSearch.trim();
        if (q !== urlQuery) patch({ q });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    // Категории и бренды выбранного раздела
    useEffect(() => {
        const ctrl = new AbortController();
        setFilters({ categories: [], brands: [] });
        shopApi.filters(productType, ctrl.signal).then(setFilters).catch(() => {});
        return () => ctrl.abort();
    }, [productType]);

    // Список товаров
    useEffect(() => {
        const ctrl = new AbortController();
        setState((s) => ({ ...s, status: 'loading' }));
        shopApi.products({ ...params, product_type: productType }, ctrl.signal)
            .then((res) => setState({ status: 'ok', items: res.data, meta: res.meta }))
            .catch(() => { if (!ctrl.signal.aborted) setState({ status: 'error', items: [], meta: null }); });
        return () => ctrl.abort();
    }, [params, productType, reloadKey]);

    const categories = filters.categories.filter(
        (c) => !params.vehicle_type || c.vehicle_type === 'all' || c.vehicle_type === params.vehicle_type
    );
    const { meta, items, status } = state;

    const goToPage = (page) => {
        patch({ page: String(page) });
        catalogRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const resetFilters = () => { setSearch(''); setSp({}); };
    const hasFilters = Object.keys(params).some((k) => k !== 'sort' && k !== 'page');

    return (
        <>
            <Helmet>
                <title>{section.seoTitle}</title>
                <meta name="description" content={section.seoDesc} />
            </Helmet>

            <section className="shop-hero" style={{ '--shop-hero-image': `url(${section.heroImage})` }}>
                <div className="shop-container shop-hero__inner">
                    <span className="shop-hero__badge"><ShieldCheck size={13} /> {hero.badge}</span>
                    <h1>{hero.title}<span>{hero.accent}</span></h1>
                    <p>{hero.text}</p>
                    <div className="shop-hero__actions">
                        <button
                            type="button"
                            className="shop-btn"
                            onClick={() => catalogRef.current?.scrollIntoView({ behavior: 'smooth' })}
                        >
                            {hero.cta} <ArrowRight size={16} />
                        </button>
                        <span className="shop-hero__note"><Clock size={14} /> {hero.note}</span>
                    </div>
                </div>
            </section>

            <section className="shop-features" aria-label="Преимущества">
                <div className="shop-container shop-features__grid">
                    {features.map((f) => {
                        const Icon = ICONS[f.icon] || ShieldCheck;
                        return (
                            <div className="shop-feature" key={f.title}>
                                <span className="shop-feature__icon"><Icon size={18} /></span>
                                <div><strong>{f.title}</strong><small>{f.text}</small></div>
                            </div>
                        );
                    })}
                </div>
            </section>

            <section className="shop-container shop-catalog" ref={catalogRef} id="catalog">
                <div className="shop-catalog__head">
                    <div>
                        <h2>{section.heading}</h2>
                        <p aria-live="polite">
                            {meta ? `${meta.total} ${pluralize(meta.total, ['позиция', 'позиции', 'позиций'])} в каталоге` : '\u00A0'}
                        </p>
                    </div>
                    <div className="shop-catalog__controls">
                        <label className="shop-search">
                            <Search size={16} />
                            <input
                                type="search"
                                placeholder={section.placeholder}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                aria-label="Поиск по каталогу"
                            />
                        </label>
                        <select
                            className="shop-select"
                            value={params.sort || 'featured'}
                            onChange={(e) => patch({ sort: e.target.value })}
                            aria-label="Сортировка"
                        >
                            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                    </div>
                </div>

                <div className="shop-chips" role="tablist" aria-label="Тип техники">
                    {VEHICLE_TABS.map((t) => (
                        <button
                            key={t.value}
                            type="button"
                            role="tab"
                            aria-selected={(params.vehicle_type || '') === t.value}
                            className={`shop-chip shop-chip--vehicle ${(params.vehicle_type || '') === t.value ? 'is-active' : ''}`}
                            onClick={() => patch({ vehicle_type: t.value, category: '' })}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className="shop-chips" aria-label="Категории">
                    <button
                        type="button"
                        className={`shop-chip ${!params.category ? 'is-active' : ''}`}
                        onClick={() => patch({ category: '' })}
                    >
                        {section.allLabel}
                    </button>
                    {categories.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            className={`shop-chip ${params.category === c.slug ? 'is-active' : ''}`}
                            onClick={() => patch({ category: c.slug })}
                        >
                            {c.name}
                        </button>
                    ))}
                </div>

                {status === 'loading' && items.length === 0 && (
                    <div className="shop-grid" aria-busy="true">
                        {Array.from({ length: 8 }, (_, i) => <div key={i} className="shop-skeleton shop-grid__skeleton" />)}
                    </div>
                )}

                {status === 'error' && (
                    <div className="shop-state" role="alert">
                        <h2>Не удалось загрузить каталог</h2>
                        <p>Проверьте соединение и попробуйте ещё раз.</p>
                        <button type="button" className="shop-btn shop-btn--ghost" onClick={() => setReloadKey((k) => k + 1)}>
                            Повторить
                        </button>
                    </div>
                )}

                {status !== 'error' && !(status === 'loading' && items.length === 0) && (
                    items.length > 0 ? (
                        <div className={`shop-grid ${status === 'loading' ? 'is-loading' : ''}`}>
                            {items.map((p) => <PartCard key={p.id} part={p} />)}
                        </div>
                    ) : (
                        <div className="shop-state">
                            <SearchX size={36} strokeWidth={1.4} />
                            <h2>Ничего не найдено</h2>
                            <p>Попробуйте изменить запрос или убрать фильтры.</p>
                            {hasFilters && <button type="button" className="shop-btn shop-btn--ghost" onClick={resetFilters}>Сбросить фильтры</button>}
                        </div>
                    )
                )}

                {meta && meta.last_page > 1 && (
                    <nav className="shop-pagination" aria-label="Страницы">
                        <button type="button" disabled={meta.current_page <= 1} onClick={() => goToPage(meta.current_page - 1)} aria-label="Предыдущая страница">
                            <ChevronLeft size={16} />
                        </button>
                        <span>Страница {meta.current_page} из {meta.last_page}</span>
                        <button type="button" disabled={meta.current_page >= meta.last_page} onClick={() => goToPage(meta.current_page + 1)} aria-label="Следующая страница">
                            <ChevronRight size={16} />
                        </button>
                    </nav>
                )}
            </section>
        </>
    );
}